/**
 * Money math (CRM-20..CRM-25, BIL-9). Integer cents only; every function is pure and
 * property-tested (CLAUDE.md: fast-check for money math).
 */

export const JOB_PAYMENT_STATUSES = [
  "none",
  "deposit_due",
  "deposit_paid",
  "balance_due",
  "paid",
  "refunded",
] as const;
export type JobPaymentStatus = (typeof JOB_PAYMENT_STATUSES)[number];

export interface JobMoneyInput {
  /** From the service's deposit rule at booking (CRM-20). */
  depositRequiredCents: number;
  /** What the office says the job costs (until invoices in Phase 7); includes the deposit. */
  amountDueCents: number;
  /** Payments in minus refunds out, for this job. */
  netPaidCents: number;
  /** Any refund was ever issued on this job. */
  hadRefunds: boolean;
  /** Canceled jobs owe nothing more (refunding a deposit is a separate, explicit act). */
  canceled: boolean;
}

export interface JobMoney {
  status: JobPaymentStatus;
  /** Still to collect; never negative. */
  owedCents: number;
  /** How much of the deposit is covered (payments count toward the deposit first). */
  depositPaidCents: number;
}

/**
 * The deposit is part of the job's total, not on top of it (CRM-20 "applied to the final
 * invoice"): a $450 job with a $100 deposit owes $350 after the deposit is paid.
 */
export function jobMoney(m: JobMoneyInput): JobMoney {
  const paid = Math.max(0, m.netPaidCents);
  const deposit = Math.max(0, m.depositRequiredCents);
  const total = Math.max(deposit, Math.max(0, m.amountDueCents));
  const depositPaidCents = Math.min(paid, deposit);
  const owedCents = m.canceled ? 0 : Math.max(0, total - paid);
  let status: JobPaymentStatus;
  if (m.canceled || total === 0) status = paid > 0 ? "paid" : m.hadRefunds ? "refunded" : "none";
  else if (paid < deposit) status = "deposit_due";
  // Deposit covered and the full price not set yet: not "paid", the rest is still to come.
  else if (m.amountDueCents <= 0) status = "deposit_paid";
  else if (owedCents === 0) status = "paid";
  else status = "balance_due";
  return { status, owedCents, depositPaidCents };
}

/** BIL-9: the platform fee on a payment, from the business's rate in basis points (0 = waived). */
export function applicationFeeCents(amountCents: number, feeBp: number): number {
  if (amountCents <= 0 || feeBp <= 0) return 0;
  return Math.round((amountCents * feeBp) / 10_000);
}

/**
 * CRM-22: the share of the platform fee returned with a refund. Computed on the running total so
 * several partial refunds add up exactly: a full refund returns the whole fee, and the fee
 * returned never exceeds the fee taken.
 */
export function feeRefundCents(
  feeCents: number,
  amountCents: number,
  alreadyRefundedCents: number,
  refundCents: number,
  alreadyFeeRefundedCents: number,
): number {
  if (amountCents <= 0 || feeCents <= 0 || refundCents <= 0) return 0;
  const refundedAfter = Math.min(amountCents, alreadyRefundedCents + refundCents);
  const feeDueAfter = Math.round((feeCents * refundedAfter) / amountCents);
  return Math.max(0, Math.min(feeCents, feeDueAfter) - alreadyFeeRefundedCents);
}

/** "$1,234.50" */
export function formatCents(cents: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}

/** CRM-21: the sentence customers read on the payment page before they pay a deposit. */
export function describeRefundPolicy(p: {
  refundPolicy: "non_refundable" | "refundable" | "refundable_until";
  refundableUntilHours: number;
}): string {
  switch (p.refundPolicy) {
    case "refundable":
      return "Deposits are fully refundable.";
    case "refundable_until":
      return `Deposits are refundable until ${p.refundableUntilHours} hours before the visit.`;
    default:
      return "Deposits are non-refundable.";
  }
}
